import type { InputQueueSnapshot } from './workHealth';
/** Preserve user-input order when an edit needs asynchronous preparation.
 * Idle commands remain synchronous. Commands emitted by a prepared edit belong
 * to that edit, ahead of input that arrived while preparation was pending. */
export class InputCommandQueue {
	private entries: Array<{
		ready: boolean;
		enqueuedAt: number;
		run: () => void;
		cancel?: () => void;
	}> = [];
	private readonly report: (error: unknown) => void;
	constructor(
		report: (error: unknown) => void = (error: unknown): void =>
			console.error('Aqueduct input failed', error)
	) {
		this.report = report;
	}

	private running = false;
	private closed = false;

	enqueue(run: () => void, cancel?: () => void): void {
		if (this.closed) {
			cancel?.();
			return;
		}
		if (this.running) {
			run();
			return;
		}
		this.entries.push({ ready: true, enqueuedAt: performance.now(), run, cancel });
		this.drain();
	}

	prepare<T>(
		value: Promise<T>,
		commit: (value: T) => void,
		failure: (error: unknown) => void = this.report,
		cancel?: () => void
	): void {
		const entry = { ready: false, enqueuedAt: performance.now(), run: () => {}, cancel };
		if (!this.closed) this.entries.push(entry);
		else cancel?.();
		void value.then(
			(result: T) => {
				entry.run = () => commit(result);
				entry.ready = true;
				this.drain();
			},
			(error: unknown) => {
				if (this.closed) return;
				failure(error);
				entry.run = () => {};
				entry.ready = true;
				this.drain();
			}
		);
	}

	snapshot(): InputQueueSnapshot {
		return {
			depth: this.entries.length,
			preparing: this.entries.filter((entry) => !entry.ready).length,
			running: this.running,
			oldestAgeMs: this.entries.length
				? Math.max(0, performance.now() - this.entries[0].enqueuedAt)
				: 0
		};
	}

	close(): void {
		this.closed = true;
		for (const entry of this.entries) entry.cancel?.();
		this.entries = [];
	}

	private drain(): void {
		if (this.running || this.closed) return;
		this.running = true;
		try {
			while (!this.closed && this.entries[0]?.ready) {
				try {
					this.entries.shift()!.run();
				} catch (error: unknown) {
					this.report(error);
				}
			}
		} finally {
			this.running = false;
		}
	}
}
